import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { theme } from 'src/features/common/app/components/theme-provider/theme';
import { StyledWrapper } from 'src/features/common/activity/components/activity-label/styles';
import type { LotI } from '@tacans/shared-marketplace-layer';
import { ILotStatusLocale } from 'src/features/common/collectible/types/locales';
import { getAuctionStatus, getLotType } from 'src/utils/lot.utils';

export const LotStatusLabel: React.VFC<{ lot: LotI }> = ({ lot }) => {
    const { t } = useTranslation(['components']);
    const { isAuction } = getLotType(lot.type);
    const {
        isAuctionStarted,
        isAuctionInProgress
    } = getAuctionStatus(lot.auctionStartAt, lot.auctionEndAt);

    const locale: ILotStatusLocale = useMemo(() => (
        t('lotStatus', { returnObjects: true })
    ), [t]);

    const statusLabel = useMemo(() => {
        if (!isAuction && lot.isActive) {
            return 'onSale';
        }
        if (isAuction && !lot.isActive) {
            return 'notActiveAuction';
        }
        if (lot.isActive && isAuctionInProgress) {
            return 'activeAuction';
        }
        if (lot.isActive && !isAuctionStarted) {
            return 'delayedAuction';
        }
        if (isAuctionStarted && !isAuctionInProgress) {
            return 'auctionIsEnded';
        }
        if (!lot.isActive) {
            return 'notOnSale';
        }
        return '';
    }, [isAuction, isAuctionInProgress, isAuctionStarted, lot.isActive]);

    const typeColors = useMemo<Partial<Record<string, string>>>(() => ({
        onSale: theme.colors.pink500,
        activeAuction: theme.colors.green500,
    }), []);

    return statusLabel ? (
        <StyledWrapper style={{ color: typeColors[statusLabel] }}>
            {locale[statusLabel]}
        </StyledWrapper>
    ) : null;
};
